import React, { useEffect, useState } from 'react';
import { gameLogger } from '../../../utils/GameLogger';
import { ServiceRegistry } from '../../core/ServiceRegistry';
import { uiActionBridge } from '../framework/uiActionBridge';
import { Button } from '../primitives/Button';
import { StatRow } from '../primitives/StatRow';
import { COLORS, SPACE, RADII, FONT_SIZE, FONT_WEIGHT, FONTS, INK, ACCENT } from '../theme';

/**
 * SimulationContent — propless body of the Simulation Control panel (Phase 2
 * fold). WindowHost mounts it inside the shared ToolWindow chrome (only while
 * the `simulationPanel` window is open). Player-free (SimulationSystem singleton);
 * spawn-test actions dispatch via uiActionBridge → ActionDispatcher (the same
 * path the old onAction prop used).
 */
export const SimulationContent: React.FC = () => {
  const [botCount, setBotCount] = useState(1);
  const [chaosIntensity, setChaosIntensity] = useState(0.2);
  const [isChaosActive, setIsChaosActive] = useState(false);
  const [isSimulationRunning, setIsSimulationRunning] = useState(false);
  const [activeBots, setActiveBots] = useState(0);

  const registry = ServiceRegistry.getInstance();
  // SimulationSystem is conditionally registered (only when
  // DevConfig.systems.simulation === true; that's only `full` preset).
  // For every other preset the system is intentionally absent — pass
  // `optional: true` so the registry doesn't toast "Missing service:
  // SimulationSystem" on every render. The panel surfaces an inline
  // disabled-state below when the system isn't loaded.
  const simSystem = registry.get('SimulationSystem', { optional: true });

  // Update active bot count periodically
  useEffect(() => {
    const interval = setInterval(() => {
      if (simSystem && simSystem.getManager()) {
        setActiveBots(simSystem.getManager()!.getBotCount());
      }
    }, 500);
    return () => clearInterval(interval);
  }, [simSystem]);

  const handleSpawn = () => {
    if (simSystem) {
      simSystem.spawnBots(botCount, 400, 300); // Default spawn area
      gameLogger.info('simulation', `Spawn request: ${botCount} bots`);
    }
  };

  const toggleSimulation = () => {
    if (!simSystem) return;
    if (isSimulationRunning) {
      simSystem.stopSimulation();
    } else {
      simSystem.startSimulation();
    }
    setIsSimulationRunning(!isSimulationRunning);
  };

  const toggleChaos = () => {
    if (!simSystem) return;
    const nextState = !isChaosActive;
    simSystem.toggleChaos(nextState, chaosIntensity);
    setIsChaosActive(nextState);
  };

  const handleSpawnTest = (index?: number) => {
    if (index !== undefined) {
      uiActionBridge.dispatch('spawn_test', index.toString());
    } else {
      uiActionBridge.dispatch('spawn_all_test', {});
    }
    gameLogger.info('simulation', `Spawn test account request: ${index !== undefined ? index : 'ALL'}`);
  };

  const handleIntensityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = parseFloat(e.target.value);
    setChaosIntensity(val);
    if (isChaosActive && simSystem) {
      simSystem.toggleChaos(true, val);
    }
  };

  const sectionStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: SPACE.md,
    marginBottom: SPACE.xl,
  };

  const sliderLabelStyle: React.CSSProperties = { fontSize: FONT_SIZE.sm, color: COLORS.textSecondary };

  const rangeStyle: React.CSSProperties = { width: '100%', accentColor: COLORS.info };

  return (
      <div style={{ fontFamily: FONTS.mono, fontSize: FONT_SIZE.md }}>
        {!simSystem && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              gap: SPACE.sm,
              padding: `${SPACE.lg}px ${SPACE.md}px`,
              marginBottom: SPACE.lg,
              background: `linear-gradient(180deg, ${INK.hi}, ${INK.base} 55%, ${INK.lo})`,
              border: `1px solid ${ACCENT.brass}`,
              borderRadius: RADII.lg,
              boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.06)',
              lineHeight: 1.5,
            }}
          >
            <div aria-hidden style={{ fontSize: FONT_SIZE.xl }}>⚙️</div>
            <div style={{ fontWeight: FONT_WEIGHT.bold, color: ACCENT.brassBright, fontSize: FONT_SIZE.md }}>
              Simulation engine is resting
            </div>
            <div style={{ color: COLORS.textSecondary, fontSize: FONT_SIZE.sm }}>
              This preset keeps the client-side bot simulation offline. Load{' '}
              <code>?preset=full</code> to spin it up here.
            </div>
            <div style={{ color: COLORS.textMuted, fontSize: FONT_SIZE.sm }}>
              Prefer server-side bot tournaments? Type <code>/sim start</code> in
              chat — writes to <code>data/sim/*</code>.
            </div>
          </div>
        )}
        <div style={sectionStyle}>
          <StatRow label="Active Bots:" value={activeBots} valueColor={COLORS.warning} />
          <Button
            variant="success"
            size="sm"
            fullWidth
            onClick={toggleSimulation}
            disabled={!simSystem}
            style={isSimulationRunning ? undefined : { background: COLORS.surfaceRaised }}
          >
            {isSimulationRunning ? '⏹ Stop Engine' : '▶ Start Simulation'}
          </Button>
        </div>

        <div style={sectionStyle}>
          <label style={sliderLabelStyle}>Spawn Count ({botCount})</label>
          <input
            type="range" min="1" max="50" step="1"
            value={botCount}
            onChange={(e) => setBotCount(parseInt(e.target.value))}
            style={rangeStyle}
          />
          <Button variant="secondary" size="sm" fullWidth onClick={handleSpawn}>
            🚀 Spawn Bots
          </Button>
        </div>

        <div style={sectionStyle}>
          <StatRow
            label="Chaos Mode:"
            value={isChaosActive ? 'ACTIVE' : 'IDLE'}
            valueColor={isChaosActive ? COLORS.danger : COLORS.textMuted}
          />
          <Button
            variant="danger"
            size="sm"
            fullWidth
            onClick={toggleChaos}
            style={isChaosActive ? undefined : { background: COLORS.surfaceRaised }}
          >
            {isChaosActive ? '🔒 Restrain Chaos' : '🔥 Unleash Chaos'}
          </Button>
          <label style={{ ...sliderLabelStyle, marginTop: SPACE.xs }}>Intensity ({chaosIntensity.toFixed(2)})</label>
          <input
            type="range" min="0.05" max="1.0" step="0.05"
            value={chaosIntensity}
            onChange={handleIntensityChange}
            style={rangeStyle}
          />
        </div>

        <div style={sectionStyle}>
          <div style={{
            fontWeight: FONT_WEIGHT.bold,
            fontSize: FONT_SIZE.sm,
            color: COLORS.info,
            borderBottom: `1px solid ${COLORS.borderSubtle}`,
            paddingBottom: SPACE.xs,
            marginBottom: SPACE.xs,
          }}>
            👥 TEST ACCOUNTS (0-19)
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: SPACE.xs }}>
            {Array.from({ length: 20 }).map((_, i) => (
              <Button
                key={i}
                variant="secondary"
                size="xs"
                onClick={() => handleSpawnTest(i)}
                title={`Spawn Test_${i}`}
                style={{ padding: SPACE.xs, fontSize: FONT_SIZE.xs }}
              >
                T{i}
              </Button>
            ))}
          </div>
          <Button
            variant="success"
            size="sm"
            fullWidth
            onClick={() => handleSpawnTest()}
            style={{ marginTop: SPACE.xs }}
          >
            🚀 Spawn All Testers
          </Button>
        </div>

        <Button
          variant="secondary"
          size="sm"
          fullWidth
          onClick={() => simSystem?.clear()}
          style={{ marginTop: SPACE.xs }}
        >
          🧹 Clear All Bots
        </Button>
      </div>
  );
};
